// src/components/anim/CoinsSparkleBundled.jsx
/**
 * CoinsSparkleBundled.jsx
 * -----------------------------------------------------------------------------
 * PURPOSE
 * Minimal Lottie wrapper using a *bundled* JSON import (no network fetch).
 *
 * USAGE
 *   <CoinsSparkleBundled style={{ width: 140, height: 140 }} loop />
 *
 * ACCESSIBILITY
 * - Decorative by default (aria-hidden).
 * - Respects prefers-reduced-motion: disables autoplay if user prefers less motion.
 */
// import React, { useMemo } from "react";
// import Lottie from "lottie-react";
// import coinsSparkle from "../../assets/animations/coin_sparkle.json"; // ← bundled

// export default function CoinsSparkleBundled({
//   style = { width: 140, height: 140 },
//   loop = true,
//   autoplay = true,
//   ariaHidden = true,
// }) {
//   const reduced = useMemo(
//     () => window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false,
//     []
//   );
//   return (
//     <Lottie
//       animationData={coinsSparkle}
//       loop={loop}
//       autoplay={autoplay && !reduced}
//       style={style}
//       aria-hidden={ariaHidden}
//     />
//   );
// }


// src/components/anim/LottieSmart.jsx
/**
 * LottieSmart.jsx
 * -----------------------------------------------------------------------------
 * PURPOSE
 * 1) Load a Lottie JSON from YOUR domain (self-hosted: /public or your CDN).
 * 2) If it fails, OPTIONAL: fall back to a backup URL (e.g., LottieFiles CDN).
 * 3) Respect prefers-reduced-motion and keep API tiny.
 *
 * USAGE
 * <LottieSmart
 *   src="/assets/animations/coin_sparkle.a1b2c3.json"         // PRIMARY (self-hosted)
 *   fallbackSrc="https://lottie.host/xxxx/coin_sparkle.json" // OPTIONAL BACKUP
 *   loop
 *   autoplay
 *   style={{ width: 140, height: 140 }}
 * />
 *
 * DEPENDENCY
 *   npm i lottie-react
 *
 * ACCESSIBILITY
 * - Decorative animations are aria-hidden by default.
 * - For meaningful visuals, wrap with a caption.
 */
/**
 * LottieSmart.jsx (FINAL)
 * -----------------------------------------------------------------------------
 * PURPOSE
 *  - Accept BOTH URL strings AND imported JSON objects.
 *  - Fetch remote JSON only when a string URL is provided.
 *  - Provide optional fallbackSrc if primary load fails.
 *  - Support prefers-reduced-motion.
 *
 * USAGE (JSON import)
 *  import meditatingBull from "../../assets/animations/meditating_bull.json";
 *
 *  <LottieSmart src={meditatingBull} loop autoplay />
 *
 * USAGE (URL from public folder)
 *  <LottieSmart src="/assets/animations/meditating_bull.json" loop autoplay />
 *
 * BRAND
 *  - FinTorro coded & documented standard.
 */

import React, { useEffect, useMemo, useState } from "react";
import Lottie from "lottie-react";

export default function LottieSmart({
  src,                     // JSON object OR URL string
  fallbackSrc = "",        // optional fallback URL string
  loop = true,
  autoplay = true,
  style = { width: 140, height: 140 },
  ariaHidden = true,
}) {
  const [data, setData] = useState(null);

  // Accessibility: disabled animations for reduced-motion users
  const reducedMotion = useMemo(() => {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  }, []);

  /**
   * Load data from:
   *  - JSON object (immediate)
   *  - URL string (fetch)
   */
  async function loadSource(srcValue) {
    // Case 1: Already a JSON object (imported)
    if (typeof srcValue === "object") {
      return srcValue;
    }

    // Case 2: URL string → fetch JSON
    if (typeof srcValue === "string") {
      const res = await fetch(srcValue, { cache: "force-cache" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }

    throw new Error("Unsupported src type for LottieSmart");
  }

  // MAIN LOADER
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const json = await loadSource(src);
        if (alive) setData(json);
      } catch (e1) {
        if (!fallbackSrc) return;
        try {
          const json2 = await loadSource(fallbackSrc);
          if (alive) setData(json2);
        } catch (e2) {
          console.warn("LottieSmart failed to load both src and fallbackSrc", {
            primary: e1,
            fallback: e2,
          });
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [src, fallbackSrc]);

  // While loading: render nothing (can customize if needed)
  if (!data) return null;

  return (
    <Lottie
      animationData={data}
      loop={loop}
      autoplay={autoplay && !reducedMotion}
      style={style}
      aria-hidden={ariaHidden}
    />
  );
}
